import { Inject, Injectable } from '@angular/core';
import { UserAccess } from '@thehubz/shared/user-access';
import { UserSession } from '../models';
import { StorageUtilityLib } from '../util/storage-utility';
import { DataAccessType } from './access-types';
import { USER_SESSION_TOKEN } from './user-session.token';

export type ClientRole =
  | 'create'
  | 'update'
  | 'delete'
  | 'download'
  | 'downloadonlylowres'
  | 'expired.download'
  | 'upload'
  | 'share'
  | 'addcomment'
  | 'performannotate'
  | 'follow'
  | 'duplicate'
  | 'publishedassets'
  | 'uploadednawaitingapproval'
  | 'archived'
  | 'draft'
  | 'update.exp'
  | 'library'
  | 'infoOnly'
  | 'tab.history'
  | 'tab.data'
  | 'tab.download'
  | 'tab.version'
  | 'tab.aspectratios'
  | 'tab.usagerights'
  | 'tab.relations'
  | 'tab.tags'
  | 'tab.productiondetails'
  | 'collab'
  | 'collab.project'
  | 'collab.task'
  | 'collab.plan'
  | 'asset.upload'
  | 'asset.delete'
  | 'version'
  | 'aspectratios'
  | 'usagerights'
  | 'relations'
  | 'sub.create'
  | 'update.milest'
  | 'onhold'
  | 'close'
  | 'closeAll'
  | 'reassign'
  | 'assignme'
  | 'jumpstep'
  | 'transnoassign'
  | 'sub.closeall'
  | 'update.unavail'
  | 'update.exthour'
  | 'wiki'
  | 'export'
  | 'login.users'
  | 'export.metadata'
  | 'export.downloads'
  | 'info.update'
  | 'availability.update'
  | 'availability.access'
  | 'vacation.update'
  | 'vacation.access'
  | 'admin.group'
  | 'admin.company'
  | 'admin.teams'
  | 'admin.users'
  | 'admin.workflows'
  | 'admin.api'
  | 'dashboard.editcollection'
  | 'dashboard.editkeywords'
  | 'plandeletion'
  | 'approve'
  | 'impersonation'
  | 'import'
  | 'update.allstatus'
  | 'assignoutside.acb'
  | 'collection.publish'
  | 'collection.access'
  | 'collection.create'
  | 'collection.history'
  | 'basket.access'
  | 'commentfield'
  | 'allcommunication'
  | 'seemilestone'
  | 'tagging';

@Injectable({
  providedIn: 'root',
})
export class UserAccessService {
  private _userAccess?: UserAccess;
  private _userSession?: UserSession;

  constructor(
    @Inject(USER_SESSION_TOKEN) private userSessionToken: string,
    private readonly storageUtility: StorageUtilityLib,
  ) {}

  /* eslint-disable no-restricted-syntax */
  canPerform(object: DataAccessType, roles: ClientRole[]): boolean {
    if (!object?.accessKeys || !roles) {
      return false;
    }

    const userAccess = this.getSessionUserAccess();
    const role = object.rolePrefix + roles[0];

    if (object.accessKeys[0] === 'any') {
      // checking a role in general, not on a specific object
      return userAccess.hasRole(role);
    }
    return userAccess.hasRoleAccess(role, object.accessKeys);
  }

  hasRole(role: string): boolean {
    const userAccess = this.getSessionUserAccess();
    return userAccess.checkRole(role);
  }

  hasRoles(roles: string[]): boolean {
    const userAccess = this.getSessionUserAccess();
    return roles.every((role) => userAccess.checkRole(role));
  }

  clear() {
    this._userAccess = undefined;
  }

  get userSession(): UserSession {
    if (!this._userSession) {
      this._userSession = this.storageUtility.getLocalStorage<UserSession>(this.userSessionToken);
    }
    return this._userSession;
  }

  private getSessionUserAccess(): UserAccess {
    if (!this._userAccess) {
      const session = this.userSession;
      if (!session.userAccessData?.globalRoles && !session.userAccessData?.accessRoles) {
        // session not cleaned up
        return new UserAccess();
      }
      this._userAccess = new UserAccess(session.userAccessData.globalRoles, session.userAccessData.accessRoles);
    }
    return this._userAccess;
  }
}
